/**
 * @module SettingsFoodExclusions
 * @description Manage food exclusions for the Coach YUMIX.
 * Excluded foods will never appear in AI meal suggestions.
 */
import React, { useState, memo } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Save, Shield, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const SettingsFoodExclusions = memo(function SettingsFoodExclusions() {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();

  const [exclusions, setExclusions] = useState(user?.food_exclusions || []);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);

  const addExclusion = () => {
    const val = input.trim();
    if (val && !exclusions.includes(val)) {
      setExclusions(prev => [...prev, val]);
      setInput('');
    }
  };

  const removeExclusion = (item) => {
    setExclusions(prev => prev.filter(x => x !== item));
  };

  const saveExclusions = async () => {
    setSaving(true);
    try {
      await api.put('/users/profile', { food_exclusions: exclusions });
      updateUser({ ...user, food_exclusions: exclusions });
      toast.success(language === 'fr' ? 'Aliments exclus mis a jour!' : 'Food exclusions updated!');
    } catch {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          <Shield className="w-5 h-5 text-red-400" />
          {language === 'fr' ? 'Aliments a exclure' : 'Food Exclusions'}
        </CardTitle>
        <CardDescription>
          {language === 'fr'
            ? 'Ces aliments ne seront JAMAIS suggeres par le Coach YUMIX'
            : 'These foods will NEVER be suggested by the Coach YUMIX'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExclusion(); } }}
            placeholder={language === 'fr' ? 'Ex: fruits de mer, brocoli, tofu...' : 'Ex: seafood, broccoli, tofu...'}
            className="bg-secondary border-border flex-1"
            data-testid="food-exclusion-input"
          />
          <Button type="button" onClick={addExclusion} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="add-food-exclusion-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {exclusions.map((item) => (
            <span key={item} className="px-3 py-1 bg-red-500/20 text-red-300 text-xs rounded-full flex items-center gap-2" data-testid={`exclusion-tag-${item}`}>
              {item}
              <button type="button" onClick={() => removeExclusion(item)} className="hover:text-red-100">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        {exclusions.length > 0 && (
          <p className="text-xs text-red-400/70 italic">
            {language === 'fr' ? `${exclusions.length} aliment(s) exclus du coaching IA` : `${exclusions.length} food(s) excluded from AI coaching`}
          </p>
        )}
        <Button onClick={saveExclusions} disabled={saving} className="bg-red-500/80 hover:bg-red-500 text-white w-full" data-testid="save-food-exclusions-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {language === 'fr' ? 'Sauvegarder les exclusions' : 'Save exclusions'}
        </Button>
      </CardContent>
    </Card>
  );
});

export default SettingsFoodExclusions;
